import { forwardRef, useId, useState } from "react";
import { X } from "@phosphor-icons/react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/cn";
import { Spinner } from "@/components/ui/feedback/Spinner";
import type { InputProps } from "./Input.types";

const inputVariants = cva(
  "bfrs-w-full bfrs-rounded-[var(--bfrs-input-radius)] bfrs-border bfrs-bg-surface-card bfrs-text-text-primary bfrs-outline-none bfrs-transition bfrs-duration-150 placeholder:bfrs-text-text-placeholder disabled:placeholder:bfrs-text-text-muted focus:bfrs-border-border-focus focus:bfrs-ring-2 focus:bfrs-ring-primary-soft focus-visible:bfrs-border-border-focus focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-primary-soft disabled:bfrs-cursor-not-allowed disabled:bfrs-bg-surface-app disabled:bfrs-text-text-disabled read-only:bfrs-bg-surface-app",
  {
    variants: {
      size: {
        xs: "bfrs-h-7 bfrs-px-3 bfrs-text-[12px]",
        sm: "bfrs-h-7 bfrs-px-3 bfrs-text-[12px]",
        md: "bfrs-h-9 bfrs-px-3 bfrs-text-[12px]",
        lg: "bfrs-h-9 bfrs-px-4 bfrs-text-[12px]"
      },
      state: {
        default: "bfrs-border-border",
        error: "bfrs-border-danger focus:bfrs-border-danger focus:bfrs-ring-danger/15 focus-visible:bfrs-border-danger focus-visible:bfrs-ring-danger/15"
      },
      hasLeftIcon: { true: "" },
      hasRightIcon: { true: "" }
    },
    compoundVariants: [
      { size: "xs", hasLeftIcon: true, className: "bfrs-pl-8" },
      { size: "sm", hasLeftIcon: true, className: "bfrs-pl-8" },
      { size: "md", hasLeftIcon: true, className: "bfrs-pl-9" },
      { size: "lg", hasLeftIcon: true, className: "bfrs-pl-10" },
      { size: "xs", hasRightIcon: true, className: "bfrs-pr-8" },
      { size: "sm", hasRightIcon: true, className: "bfrs-pr-8" },
      { size: "md", hasRightIcon: true, className: "bfrs-pr-9" },
      { size: "lg", hasRightIcon: true, className: "bfrs-pr-10" }
    ],
    defaultVariants: {
      size: "md",
      state: "default"
    }
  }
);

const inputChromeVariants = cva(
  "bfrs-flex bfrs-w-full bfrs-overflow-hidden bfrs-rounded-[var(--bfrs-input-radius)] bfrs-border bfrs-bg-surface-card bfrs-text-text-primary bfrs-transition bfrs-duration-150 focus-within:bfrs-border-border-focus focus-within:bfrs-ring-2 focus-within:bfrs-ring-primary-soft",
  {
    variants: {
      size: {
        xs: "bfrs-h-7 bfrs-text-[12px]",
        sm: "bfrs-h-7 bfrs-text-[12px]",
        md: "bfrs-h-9 bfrs-text-[12px]",
        lg: "bfrs-h-9 bfrs-text-[12px]"
      },
      state: {
        default: "bfrs-border-border",
        error: "bfrs-border-danger focus-within:bfrs-border-danger focus-within:bfrs-ring-danger/15"
      },
      disabled: {
        true: "bfrs-bg-surface-app bfrs-text-text-disabled"
      }
    },
    defaultVariants: {
      size: "md",
      state: "default"
    }
  }
);

const addonSizeClassName = {
  xs: "bfrs-px-2.5 bfrs-text-[11.5px]",
  sm: "bfrs-px-2.5 bfrs-text-[12px]",
  md: "bfrs-px-3 bfrs-text-[12.5px]",
  lg: "bfrs-px-3.5 bfrs-text-sm"
} as const;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName,
      labelClassName,
      inputClassName,
      size = "md",
      error,
      errorMessage,
      helperText,
      label,
      required,
      prefix,
      suffix,
      leftIcon,
      rightIcon,
      loading,
      onClear,
      clearLabel = "Clear input",
      fullWidth = true,
      id,
      value,
      defaultValue,
      onChange,
      disabled,
      readOnly,
      "aria-invalid": ariaInvalid,
      "aria-describedby": ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const helperId = helperText ? `${inputId}-helper` : undefined;
    const errorId = errorMessage ? `${inputId}-error` : undefined;
    const describedBy = [ariaDescribedBy, helperId, errorId].filter(Boolean).join(" ") || undefined;
    const isControlled = value !== undefined;
    const [uncontrolledHasValue, setUncontrolledHasValue] = useState(() => {
      const v = defaultValue;
      return v !== undefined && v !== null && String(v).length > 0;
    });
    const hasValue = isControlled
      ? value !== null && String(value).length > 0
      : uncontrolledHasValue;
    const canClear = Boolean(onClear && hasValue && !disabled && !readOnly);
    const hasRightControl = Boolean(rightIcon || canClear || loading);
    const hasAddons = Boolean(prefix || suffix);
    const inputState = error || errorMessage ? "error" : "default";
    const nativeInput = (
      <input
        ref={ref}
        id={inputId}
        value={value}
        defaultValue={defaultValue}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={ariaInvalid ?? error ?? Boolean(errorMessage)}
        aria-describedby={describedBy}
        onChange={(e) => {
          if (!isControlled) setUncontrolledHasValue(e.target.value.length > 0);
          onChange?.(e);
        }}
        data-filled={!hasAddons && hasValue && inputState === "default" ? "" : undefined}
        className={cn(
          hasAddons
            ? cn(
                "bfrs-h-full bfrs-w-full bfrs-min-w-0 bfrs-appearance-none bfrs-border-0 bfrs-bg-transparent bfrs-px-[10px] bfrs-text-inherit bfrs-outline-none placeholder:bfrs-text-text-placeholder disabled:placeholder:bfrs-text-text-muted disabled:bfrs-cursor-not-allowed disabled:bfrs-text-text-disabled",
                leftIcon && "bfrs-pl-9",
                hasRightControl && "bfrs-pr-9"
              )
            : cn(inputVariants({ size, state: inputState, hasLeftIcon: Boolean(leftIcon), hasRightIcon: hasRightControl }), size === "sm" && "data-[filled]:bfrs-border-border-focus"),
          inputClassName
        )}
        {...props}
      />
    );

    return (
      <div className={cn(fullWidth ? "bfrs-w-full" : "bfrs-inline-block", containerClassName, className)}>
        {label && (
          <label htmlFor={inputId} className={cn("bfrs-mb-1.5 bfrs-block bfrs-text-[12px] bfrs-font-semibold bfrs-text-text-secondary", labelClassName)}>
            {label}
            {required && <span className="bfrs-ml-1 bfrs-text-danger">*</span>}
          </label>
        )}
        {hasAddons ? (
          <div data-filled={hasValue && inputState === "default" ? "" : undefined} className={cn(inputChromeVariants({ size, state: inputState, disabled }), size === "sm" && "data-[filled]:bfrs-border-border-focus")}>
            {prefix && <span className={cn("bfrs-flex bfrs-items-center bfrs-border-r bfrs-border-border bfrs-bg-primary-soft bfrs-font-semibold bfrs-text-text-primary", addonSizeClassName[size])}>{prefix}</span>}
            <div className="bfrs-relative bfrs-min-w-0 bfrs-flex-1">
              {leftIcon && <span className="bfrs-pointer-events-none bfrs-absolute bfrs-left-3 bfrs-top-1/2 bfrs-flex -bfrs-translate-y-1/2 bfrs-text-text-muted">{leftIcon}</span>}
              {nativeInput}
              {loading && <span className="bfrs-pointer-events-none bfrs-absolute bfrs-right-3 bfrs-top-1/2 bfrs-flex -bfrs-translate-y-1/2 bfrs-text-text-muted"><Spinner size="sm" /></span>}
              {rightIcon && !canClear && !loading && <span className="bfrs-pointer-events-none bfrs-absolute bfrs-right-3 bfrs-top-1/2 bfrs-flex -bfrs-translate-y-1/2 bfrs-text-text-muted">{rightIcon}</span>}
              {canClear && (
                <button
                  type="button"
                  aria-label={clearLabel}
                  className="bfrs-absolute bfrs-right-2 bfrs-top-1/2 bfrs-flex bfrs-size-5 -bfrs-translate-y-1/2 bfrs-items-center bfrs-justify-center bfrs-rounded-sm bfrs-text-text-muted bfrs-outline-none hover:bfrs-bg-surface-muted hover:bfrs-text-text-primary focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus"
                  onClick={onClear}
                >
                  <X size={12} weight="bold" />
                </button>
              )}
            </div>
            {suffix && <span className={cn("bfrs-flex bfrs-items-center bfrs-border-l bfrs-border-border bfrs-bg-primary-soft bfrs-font-semibold bfrs-text-text-primary", addonSizeClassName[size])}>{suffix}</span>}
          </div>
        ) : (
          <div className="bfrs-relative">
            {leftIcon && <span className="bfrs-pointer-events-none bfrs-absolute bfrs-left-3 bfrs-top-1/2 bfrs-flex -bfrs-translate-y-1/2 bfrs-text-text-muted">{leftIcon}</span>}
            {nativeInput}
            {loading && <span className="bfrs-pointer-events-none bfrs-absolute bfrs-right-3 bfrs-top-1/2 bfrs-flex -bfrs-translate-y-1/2 bfrs-text-text-muted"><Spinner size="sm" /></span>}
            {rightIcon && !canClear && !loading && <span className="bfrs-pointer-events-none bfrs-absolute bfrs-right-3 bfrs-top-1/2 bfrs-flex -bfrs-translate-y-1/2 bfrs-text-text-muted">{rightIcon}</span>}
            {canClear && (
              <button
                type="button"
                aria-label={clearLabel}
                className="bfrs-absolute bfrs-right-2 bfrs-top-1/2 bfrs-flex bfrs-size-5 -bfrs-translate-y-1/2 bfrs-items-center bfrs-justify-center bfrs-rounded-sm bfrs-text-text-muted bfrs-outline-none hover:bfrs-bg-surface-muted hover:bfrs-text-text-primary focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus"
                onClick={onClear}
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </div>
        )}
        {errorMessage ? (
          <p id={errorId} className="bfrs-mt-1.5 bfrs-text-xs bfrs-leading-5 bfrs-text-danger">
            {errorMessage}
          </p>
        ) : helperText ? (
          <p id={helperId} className="bfrs-mt-1.5 bfrs-text-xs bfrs-leading-5 bfrs-text-text-muted">
            {helperText}
          </p>
        ) : null}
      </div>
    );
  }
);

Input.displayName = "Input";
